import { create } from 'zustand'
import api from './services/api'

export interface Conversation {
  id: string
  title: string
  source: string
  context: string
  created_at: string
  updated_at: string
  message_count: number
}

export interface Message {
  id: string
  conversation_id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
  sources: string[]
  provider_used?: string
  model_name?: string
}

export interface Skill {
  id: string
  name: string
  description: string
  system_prompt: string
  tags: string[]
  created_at: string
  updated_at: string
}

export interface KnowledgeChunk {
  id: string
  filename: string
  content: string
  chunk_index: number
  created_at: string
}

export interface Project {
  id: string
  name: string
  icon: string
  system_prompt: string
  color: string
  created_at: string
  updated_at: string
}

export interface AppStore {
  // State
  conversations: Conversation[]
  currentConversation: Conversation | null
  messages: Message[]
  skills: Skill[]
  knowledge: KnowledgeChunk[]
  projects: Project[]
  activeProjectId: string
  currentContext: string
  selectedProviderId: string | null
  selectedSkillId: string | null
  isLoading: boolean
  isStreaming: boolean
  streamingContent: string | null
  error: string | null

  // Actions
  checkHealth: () => Promise<void>
  fetchConversations: () => Promise<void>
  createConversation: (title: string, context?: string) => Promise<Conversation>
  selectConversation: (id: string) => Promise<void>
  deleteConversation: (id: string) => Promise<void>
  fetchSkills: () => Promise<void>
  createSkill: (name: string, description: string, prompt: string, tags?: string[]) => Promise<Skill>
  deleteSkill: (id: string) => Promise<void>
  fetchKnowledge: () => Promise<void>
  fetchProjects: () => Promise<void>
  setActiveProject: (projectId: string) => void
  sendMessage: (message: string, skillId?: string, useKnowledge?: boolean, providerId?: string) => Promise<void>
  setCurrentContext: (context: string) => void
  setSelectedProviderId: (id: string | null) => void
  setSelectedSkillId: (id: string | null) => void
  setError: (error: string | null) => void
}

export const useStore = create<AppStore>((set, get) => ({
  // Initial state
  conversations: [],
  currentConversation: null,
  messages: [],
  skills: [],
  knowledge: [],
  projects: [],
  activeProjectId: 'general',
  currentContext: 'default',
  selectedProviderId: null,
  selectedSkillId: null,
  isLoading: false,
  isStreaming: false,
  streamingContent: null,
  error: null,

  // Actions
  checkHealth: async () => {
    try {
      await api.get('/health')
    } catch (err) {
      set({ error: 'Backend non accessible' })
    }
  },

  fetchConversations: async () => {
    set({ isLoading: true })
    try {
      const convs = await api.get<Conversation[]>('/conversations/')
      set({ conversations: convs.data })
    } catch (err) {
      set({ error: 'Erreur chargement conversations' })
    } finally {
      set({ isLoading: false })
    }
  },

  createConversation: async (title: string, context = 'default') => {
    try {
      const activeProjectId = get().activeProjectId
      const conv = await api.post<Conversation>('/conversations/', {
        title,
        context,
        source: 'manual',
        project_id: activeProjectId
      })
      set(state => ({
        conversations: [...state.conversations, conv.data]
      }))
      return conv.data
    } catch (err) {
      set({ error: 'Erreur création conversation' })
      throw err
    }
  },

  selectConversation: async (id: string) => {
    const conv = get().conversations.find(c => c.id === id)
    if (conv) {
      set({ currentConversation: conv })
      // Charger les messages
      try {
        const resp = await api.get(`/conversations/${id}/messages/`)
        set({ messages: resp.data || [] })
      } catch (err) {
        set({ error: 'Erreur chargement messages' })
      }
    }
  },

  deleteConversation: async (id: string) => {
    try {
      await api.delete(`/conversations/${id}/`)
      set(state => ({
        conversations: state.conversations.filter(c => c.id !== id),
        currentConversation: state.currentConversation?.id === id ? null : state.currentConversation
      }))
    } catch (err) {
      set({ error: 'Erreur suppression conversation' })
    }
  },

  fetchSkills: async () => {
    try {
      const skills = await api.get<Skill[]>('/api/skills')
      set({ skills: skills.data })
    } catch (err) {
      set({ error: 'Erreur chargement skills' })
    }
  },

  createSkill: async (name: string, description: string, prompt: string, tags = []) => {
    try {
      const skill = await api.post<Skill>('/api/skills', {
        name,
        description,
        system_prompt: prompt,
        tags
      })
      set(state => ({
        skills: [...state.skills, skill.data]
      }))
      return skill.data
    } catch (err) {
      set({ error: 'Erreur création skill' })
      throw err
    }
  },

  deleteSkill: async (id: string) => {
    try {
      await api.delete(`/api/skills/${id}`)
      set(state => ({
        skills: state.skills.filter(s => s.id !== id)
      }))
    } catch (err) {
      set({ error: 'Erreur suppression skill' })
    }
  },

  fetchKnowledge: async () => {
    try {
      const knowledge = await api.get<KnowledgeChunk[]>('/knowledge/chunks')
      set({ knowledge: knowledge.data })
    } catch (err) {
      set({ error: 'Erreur chargement knowledge' })
    }
  },

  fetchProjects: async () => {
    try {
      const projects = await api.get<Project[]>('/api/projects/')
      set({ projects: projects.data })
    } catch (err) {
      set({ error: 'Erreur chargement projets' })
    }
  },

  setActiveProject: (projectId: string) => {
    set({ activeProjectId: projectId })
  },

  sendMessage: async (message: string, skillId?: string, useKnowledge = true, providerId?: string) => {
    const conv = get().currentConversation
    if (!conv) {
      set({ error: 'Pas de conversation sélectionnée' })
      return
    }

    const activeProjectId = get().activeProjectId
    const projects = get().projects
    const activeProject = projects.find(p => p.id === activeProjectId)
    const systemPrompt = activeProject?.system_prompt || ''

    const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8100'
    const token = localStorage.getItem('claudie_token')

    set({ isLoading: true, isStreaming: true, streamingContent: '' })

    try {
      const response = await fetch(`${API_URL}/chat/conversations/${conv.id}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          message,
          provider_id: providerId || 'ollama-qwen',
          system_prompt: systemPrompt
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`)
      }

      const reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let streamedContent = ''
      let finalData: any = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') break

          try {
            const parsed = JSON.parse(data)
            if (parsed.type === 'chunk') {
              streamedContent += parsed.content
              set({ streamingContent: streamedContent })
            } else if (parsed.type === 'title') {
              const newTitle = parsed.title
              set(state => ({
                conversations: state.conversations.map(c =>
                  c.id === conv.id ? { ...c, title: newTitle } : c
                ),
                currentConversation: state.currentConversation?.id === conv.id
                  ? { ...state.currentConversation, title: newTitle }
                  : state.currentConversation
              }))
            } else if (parsed.type === 'done') {
              finalData = parsed
            } else if (parsed.type === 'error') {
              throw new Error(parsed.error)
            }
          } catch (e: any) {
            if (e.message && !e.message.includes('JSON')) {
              throw e
            }
          }
        }
      }

      // Ajouter le message assistant au state
      if (finalData) {
        set(state => ({
          messages: [
            ...state.messages,
            {
              id: finalData.assistant_message_id,
              conversation_id: conv.id,
              role: 'assistant' as const,
              content: streamedContent,
              created_at: new Date().toISOString(),
              sources: [],
              provider_used: finalData.provider_name,
              model_name: finalData.model_name
            }
          ],
          streamingContent: null,
          isStreaming: false
        }))
      } else {
        set({ streamingContent: null, isStreaming: false })
      }
    } catch (err: any) {
      set({ error: err.message || 'Erreur envoi message', isStreaming: false, streamingContent: null })
      console.error('Error sending message:', err)
    } finally {
      set({ isLoading: false })
    }
  },

  setCurrentContext: (context: string) => {
    set({ currentContext: context })
  },
  setSelectedProviderId: (id: string | null) => {
    set({ selectedProviderId: id })
  },
  setSelectedSkillId: (id: string | null) => {
    set({ selectedSkillId: id })
  },
  setError: (error: string | null) => {
    set({ error })
  }
}))
